import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { MessageSquarePlus, PhoneCall, Video, Users as UsersIcon, MailOpen, Linkedin } from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { LogConversationModal } from '../components/crm/LogConversationModal';
import { typeLabel, resultLabel, fmtDateTime, fmtDate, contactName } from '../components/crm/constants';

const TYPE_ICONS = {
  phone_call: PhoneCall, teams_meeting: Video, personal_meeting: UsersIcon,
  email: MailOpen, linkedin: Linkedin,
};

export default function SalesActivities() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const load = useCallback(async () => {
    const res = await crmFetch('/activities');
    if (res.tableMissing) setTableMissing(true);
    else if (res.success) { setActivities(res.data || []); setTableMissing(false); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <SalesLayout>
      <div data-testid="crm-activities-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Activities</h1>
            <p className="text-sm text-gray-500 mt-1">Every logged conversation across your accounts.</p>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setLogOpen(true)} data-testid="activities-log-conversation-btn">
            <MessageSquarePlus className="w-4 h-4 mr-2" /> Log Conversation
          </Button>
        </div>

        {tableMissing && (
          <Card className="border-amber-300 bg-amber-50" data-testid="crm-migration-notice">
            <CardContent className="p-6 text-sm text-amber-800">
              CRM tables not migrated yet — run <code className="px-1 bg-amber-100 rounded">2026-08-30-crm-phase1.sql</code> in the Supabase SQL Editor.
            </CardContent>
          </Card>
        )}

        {!tableMissing && (
          <Card>
            <CardContent className="p-6">
              {loading && <p className="text-sm text-gray-400 text-center py-8">Loading…</p>}
              {!loading && activities.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-8">No activities yet — log your first conversation.</p>
              )}
              <div className="space-y-5">
                {activities.map((a) => {
                  const Icon = TYPE_ICONS[a.type] || PhoneCall;
                  return (
                    <div key={a.id} className="flex gap-4" data-testid={`activity-item-${a.id}`}>
                      <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0 pb-5 border-b last:border-0">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="font-medium text-gray-900">{typeLabel(a.type)}</span>
                          {a.account && (
                            <Link to={`/accounts/${a.account.id}`} className="text-blue-600 hover:underline font-medium">{a.account.name}</Link>
                          )}
                          {a.contact && <span className="text-gray-500">with {contactName(a.contact)}</span>}
                          {a.reached === false && <Badge className="bg-rose-100 text-rose-700 border-0 text-[10px]">Not reached</Badge>}
                          {a.result && <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{resultLabel(a.result)}</Badge>}
                          <span className="text-xs text-gray-400 ml-auto">{fmtDateTime(a.created_at)}</span>
                        </div>
                        {a.discussed && <p className="text-sm text-gray-600 mt-1.5 whitespace-pre-wrap">{a.discussed}</p>}
                        {(a.next_step || a.follow_up_date) && (
                          <p className="text-xs text-gray-500 mt-1.5">
                            {a.next_step && <>Next: <span className="font-medium text-gray-700">{a.next_step}</span></>}
                            {a.follow_up_date && <span className="ml-2 text-amber-600 font-medium">Follow up {fmtDate(a.follow_up_date)}</span>}
                          </p>
                        )}
                        {a.logger && <p className="text-[11px] text-gray-400 mt-1">by {`${a.logger.first_name || ''} ${a.logger.last_name || ''}`.trim()}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <LogConversationModal open={logOpen} onOpenChange={setLogOpen} onSaved={load} />
    </SalesLayout>
  );
}
